import { Chip, Spinner } from '@sjuni/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { FormError } from '@/components/FormError';

import { useMigrationRun } from './useMigration';

/**
 * §5.9's "live progress log", and the report underneath it.
 *
 * The log is the command's own output, polled while the run is going. It is a
 * `<pre>` rather than anything parsed: these commands print progress bars and
 * warnings that name Georgian filenames, and a console that tried to structure
 * that would be hiding the half of it nobody anticipated. The report beside it is
 * the structured half — the command's own JSON, which is where the counts and the
 * reasons live.
 */
export function RunLog({ id }: { id: number }) {
  const { t } = useTranslation();
  const query = useMigrationRun(id);
  const log = useRef<HTMLPreElement>(null);

  const run = query.data?.data;
  const output = run?.output ?? '';

  /*
   * Follow the tail while it is running, and stop once it is not — scrolling a
   * finished log back to the bottom every two seconds would fight the person
   * reading the middle of it.
   */
  useEffect(() => {
    if (run?.status === 'running' && log.current) {
      log.current.scrollTop = log.current.scrollHeight;
    }
  }, [output, run?.status]);

  if (query.isPending) {
    return (
      <p className="flex items-center gap-2 text-body-sm text-secondary">
        <Spinner size="sm" label={t('common.loading')} />
        {t('common.loading')}
      </p>
    );
  }

  if (run === undefined) {
    return <FormError message={t('errors.generic')} />;
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-caption text-secondary">
        <Chip tone={run.status === 'failed' ? 'rose' : run.status === 'succeeded' ? 'teal' : 'sky'}>
          {t(`migration.status.${run.status}`)}
        </Chip>
        <code className="font-mono">{run.command}</code>
        {run.dry_run ? <Chip tone="slate">{t('migration.history.dryRun')}</Chip> : null}
        {run.duration_seconds === null ? null : (
          <span>{t('migration.log.duration', { seconds: run.duration_seconds })}</span>
        )}
        {run.exit_code === null ? null : (
          <span>{t('migration.log.exitCode', { code: run.exit_code })}</span>
        )}
      </div>

      <FormError message={run.failure} />

      {run.status === 'queued' ? (
        <p className="flex items-center gap-2 text-body-sm text-secondary">
          <Spinner size="sm" label={t('migration.log.waiting')} />
          {t('migration.log.waiting')}
        </p>
      ) : null}

      <pre
        ref={log}
        // `log` is the ARIA role for exactly this: a region that gets appended to
        // as something happens. It is also polite by default, so a run that
        // prints 3,000 lines does not interrupt a screen reader 3,000 times.
        role="log"
        /* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a
           scrollable region has to be reachable by keyboard or its content is
           unreachable without a mouse (WCAG 2.1.1). The rule's allowance is
           limited to `tabpanel`; `log` is the correct role here and needs the
           same treatment. */
        tabIndex={0}
        aria-label={t('migration.log.title')}
        className="max-h-80 overflow-auto rounded-card bg-ink p-4 font-mono text-caption leading-relaxed text-inverse"
      >
        {output.trim() === '' ? t('migration.log.empty') : output}
      </pre>

      {run.report === null ? null : (
        <section className="space-y-2">
          <h3 className="text-body-sm font-semibold text-primary">{t('migration.log.report')}</h3>

          <div className="space-y-3 rounded-card border border-subtle bg-surface p-4">
            {Object.entries(run.report).map(([key, value]) => (
              <ReportEntry key={key} name={key} value={value} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * One entry of a command's report.
 *
 * Each command writes its own shape, so this renders the four shapes they
 * actually use rather than a schema: a number, a nested group of numbers, a list
 * of strings, and a list of objects. The last is the one §5.9 means by "failures
 * with reasons" — `flagged_for_review` and `rejected` are lists of `{file, why}`,
 * and a card that showed them as "8 ×" would be dropping the only part worth
 * reading.
 *
 * Long lists are cut, with the total stated. `unresolved_links` runs to 1,503
 * entries; the whole of it is in the JSON file the command wrote, and nobody
 * reads 1,503 URLs in a modal.
 */
function ReportEntry({ name, value }: { name: string; value: unknown }) {
  const { t } = useTranslation();
  const label = name.replace(/_/g, ' ');

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <Row label={label} value="0" />;
    }

    const shown = value.slice(0, 5);

    return (
      <div>
        <Row label={label} value={value.length.toLocaleString()} />
        <ul className="mt-1 space-y-0.5 border-l-2 border-subtle pl-3">
          {shown.map((entry, index) => (
            <li key={index} className="truncate font-mono text-caption text-secondary">
              {typeof entry === 'object' && entry !== null
                ? Object.values(entry as Record<string, unknown>).join(' — ')
                : String(entry)}
            </li>
          ))}
          {value.length > shown.length ? (
            <li className="text-caption text-muted">
              {t('migration.log.andMore', { count: value.length - shown.length })}
            </li>
          ) : null}
        </ul>
      </div>
    );
  }

  if (value !== null && typeof value === 'object') {
    return (
      <div>
        <p className="text-caption font-semibold text-primary">{label}</p>
        <div className="mt-1 space-y-0.5 border-l-2 border-subtle pl-3">
          {Object.entries(value as Record<string, unknown>).map(([key, nested]) => (
            <Row
              key={key}
              label={key.replace(/_/g, ' ')}
              value={
                nested !== null && typeof nested === 'object'
                  ? Object.keys(nested).length.toLocaleString()
                  : scalar(nested)
              }
            />
          ))}
        </div>
      </div>
    );
  }

  return <Row label={label} value={scalar(value)} />;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-caption text-secondary">{label}</span>
      <span className="shrink-0 font-mono text-caption font-semibold text-primary">{value}</span>
    </div>
  );
}

function scalar(value: unknown): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? '✓' : '–';
  }

  if (typeof value === 'number') {
    return value.toLocaleString();
  }

  return String(value);
}
